import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SUMMARY_LABEL_SX, SummaryField } from './SummaryPaneComponents';
import { MultiselectSummary } from './SummaryPaneComponents';
import { useAttributeSelectors } from '../hooks';
import { useLandingPageGroupsDtos } from '../../../views/LandingPageGroups/queries';
import { useLandingPagesDtos } from '../../../views/LandingPages/queries';

const RuleRenderer = ({ rule, level = 0 }) => {
  const indentStyle = {
    marginLeft: `${level * 40}px`,
  };

  if (typeof rule === 'string') {
    return (
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }} style={indentStyle}>
        <Grid size={2}>
          <SummaryField value={rule} />
        </Grid>
      </Grid>
    );
  } else if (rule.rules) {
    return (
      <>
        {rule.rules.map((nestedRule, index) => (
          <RuleRenderer key={index} rule={nestedRule} level={level + 1} />
        ))}
      </>
    );
  } else {
    return (
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }} style={indentStyle}>
        <Grid size='grow'>
          <SummaryField value={rule.field} />
        </Grid>
        <Grid size='grow'>
          <SummaryField value={rule?.value || ''} />
        </Grid>
      </Grid>
    );
  }
};

const AttributeSummary = ({ currentCaps }) => {
  const attributeSelectors = useAttributeSelectors();
  const { data: landingPageGroups = [] } = useLandingPageGroupsDtos();
  const { data: landingPages = [] } = useLandingPagesDtos();

  const getLandingPageGroupName = (lpgId) => {
    if (typeof lpgId === 'object' && lpgId?.name) {
      return lpgId.name;
    }
    const group = landingPageGroups.find((group) => group.id === lpgId);
    return group ? group.name : `Group ${lpgId}`;
  };

  const getLandingPageName = (lpId) => {
    if (typeof lpId === 'object') {
      return lpId?.offer?.name || 'Unnamed';
    }
    const page = landingPages.find((page) => page.id === lpId);
    return page ? page.offer?.name || page.name || 'Unnamed' : `Page ${lpId}`;
  };

  return (
    <>
      {attributeSelectors.isAnySelectorNotNullOrEmpty && (
        <>
          <Typography variant='h4'>Attributes Summary</Typography>
          {attributeSelectors.userAttributeSelect && attributeSelectors.userAttributeSelect !== 'None' && (
            <Grid container spacing={2} sx={{ alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>User Attributes</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.userAttributeSelect} />
              </Grid>
              {['Include', 'Exclude'].includes(attributeSelectors.userAttributeSelect) && (
                <Grid size='grow'>
                  {attributeSelectors.userAttributeRules?.rules?.map((rule, index) => (
                    <RuleRenderer key={index} rule={rule} />
                  ))}
                </Grid>
              )}
            </Grid>
          )}
          {attributeSelectors.urlParameterSelect && attributeSelectors.urlParameterSelect !== 'None' && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>URL Parameters</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.urlParameterSelect} />
              </Grid>
              {['Include', 'Exclude'].includes(attributeSelectors.urlParameterSelect) && (
                <Grid size='grow'>
                  {attributeSelectors.urlParameterRules?.rules?.map((rule, index) => (
                    <RuleRenderer key={index} rule={rule} />
                  ))}
                </Grid>
              )}
            </Grid>
          )}
          {attributeSelectors.userAgentSelect && attributeSelectors.userAgentSelect !== 'None' && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>User Agent</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.userAgentSelect} />
              </Grid>
              {['Include', 'Exclude'].includes(attributeSelectors.userAgentSelect) && (
                <Grid size='grow'>
                  {attributeSelectors.userAgentRules?.rules?.map((rule, index) => (
                    <RuleRenderer key={index} rule={rule} />
                  ))}
                </Grid>
              )}
            </Grid>
          )}
          {attributeSelectors.criteriaIncludeOrExcludeAffNames && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>Affiliate Names</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.criteriaIncludeOrExcludeAffNames} />
              </Grid>
              {(attributeSelectors.criteriaAffiliate === 'Include' ||
                attributeSelectors.criteriaAffiliate === 'Exclude') && (
                <Grid size={4}>
                  <MultiselectSummary value={attributeSelectors.criteriaAffNames} />
                </Grid>
              )}
            </Grid>
          )}
          {attributeSelectors.affSubIDConfiguration?.andOrLogic && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>Aff SubID Configuration</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField
                  value={
                    attributeSelectors.affSubIDConfiguration?.andOrLogic === 'AND'
                      ? 'All entries must pass'
                      : attributeSelectors.affSubIDConfiguration?.andOrLogic === 'OR'
                      ? 'Any entry can pass'
                      : attributeSelectors.affSubIDConfiguration?.andOrLogic === 'ANY'
                      ? 'Each entry evaluated independently'
                      : 'No logic selected'
                  }
                  readOnly
                  disabled
                />
              </Grid>
              <Grid size='grow'>
                {attributeSelectors.affSubIDConfiguration?.subIdList?.map((item, index) => (
                  <Grid container spacing={2} sx={{ alignItems: 'start' }} key={index}>
                    <Grid size={3}>
                      <SummaryField value={item?.includeExclude || ''} />
                    </Grid>
                    <Grid size={3}>
                      <SummaryField value={item?.affiliate || ''} />
                    </Grid>
                    <Grid size={4}>
                      <SummaryField
                        multiline
                        value={
                          Array.isArray(item?.subIds)
                            ? item.subIds.join('\n')
                            : typeof item?.subIds === 'string'
                            ? item.subIds
                            : ''
                        }
                        readOnly
                        disabled
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
          {attributeSelectors.domainAffiliateConfiguration?.domainAffiliateEntries[0]?.includeExcludeAction && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>Domain-specific Affiliates</Typography>
              </Grid>
              <Grid size={10}>
                {attributeSelectors.domainAffiliateConfiguration?.domainAffiliateEntries?.map((item, index) => (
                  <Card key={index}>
                    <CardContent>
                      <div key={index}>
                        <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                          <Grid size='grow'>
                            <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Domains</FormLabel>
                            <MultiselectSummary value={item?.domains} />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ alignItems: 'start' }} style={{ paddingTop: '1rem' }}>
                          <Grid size='grow'>
                            <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Include/Exclude</FormLabel>
                            <SummaryField value={item?.includeExcludeAction} />
                          </Grid>
                          <Grid size='grow'>
                            <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Affiliates</FormLabel>
                            <MultiselectSummary value={item?.affiliates} />
                          </Grid>
                        </Grid>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
            </Grid>
          )}
          {attributeSelectors.criteriaLandingPageGroupIncludeExclude && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>Landing Page Groups</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.criteriaLandingPageGroupIncludeExclude} />
              </Grid>
              <Grid size={4}>
                <MultiselectSummary
                  value={attributeSelectors.criteriaLandingPageGroupIds?.map(getLandingPageGroupName)}
                />
              </Grid>
            </Grid>
          )}
          {attributeSelectors.criteriaLandingPageIncludeExclude && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>Landing Pages</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.criteriaLandingPageIncludeExclude} />
              </Grid>
              {(attributeSelectors.criteriaLandingPageIncludeExclude === 'Include' ||
                attributeSelectors.criteriaLandingPageIncludeExclude === 'Exclude') && (
                <Grid size={4}>
                  <MultiselectSummary value={attributeSelectors.criteriaLandingPageIds?.map(getLandingPageName)} />
                </Grid>
              )}
            </Grid>
          )}
          {attributeSelectors.criteriaIncludeOrExcludeEmailDomains && (
            <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
              <Grid size={2}>
                <Typography variant='h5'>Email Domains</Typography>
              </Grid>
              <Grid size={2}>
                <SummaryField value={attributeSelectors.criteriaIncludeOrExcludeEmailDomains} />
              </Grid>
              {(attributeSelectors.criteriaEmail === 'Include' || attributeSelectors.criteriaEmail === 'Exclude') && (
                <Grid size={4}>
                  <MultiselectSummary value={attributeSelectors.criteriaEmailDomains} />
                </Grid>
              )}
            </Grid>
          )}
          {attributeSelectors.qualityScoreCriteria && (
            <>
              {/* Quality Score Ranges */}
              {attributeSelectors.qualityScoreCriteria.qualityScoreRange && attributeSelectors.qualityScoreCriteria.qualityScoreRange.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size={12}>
                    <Typography variant='h5'>Affiliate Quality Score Ranges</Typography>
                  </Grid>
                </Grid>
              )}
              {attributeSelectors.qualityScoreCriteria.qualityScoreRange && attributeSelectors.qualityScoreCriteria.qualityScoreRange.map((range, index) => {
                const redisIndex = range.name?.replace('index_', '') || index.toString();
                const currentCap = currentCaps ? (currentCaps.quality_score_range?.[redisIndex] || 0) : 0;
                
                return (
                  <Grid container spacing={2} sx={{ alignItems: 'start' }} key={index}>
                    <Grid size={2}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>{range.name || `Range ${index + 1}`}</FormLabel>
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Min Score</FormLabel>
                      <SummaryField value={range.minScore !== null && range.minScore !== undefined ? range.minScore : ''} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Max Score</FormLabel>
                      <SummaryField value={range.maxScore !== null && range.maxScore !== undefined ? range.maxScore : ''} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Cap Amount</FormLabel>
                      <SummaryField value={range.cap || 'infinity'} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Current Cap</FormLabel>
                      <SummaryField value={currentCap} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Failure Probability</FormLabel>
                      <SummaryField value={range.failureProbability ? `${(range.failureProbability * 100).toFixed(1)}%` : ''} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Minimum Valid Multipliers</FormLabel>
                      <SummaryField value={range.validMultipliers !== null && range.validMultipliers !== undefined ? range.validMultipliers : ''} />
                    </Grid>
                    <Grid size={2}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Day Filter</FormLabel>
                      <MultiselectSummary value={Array.isArray(range.dayFilters) ? range.dayFilters : []} />
                    </Grid>
                  </Grid>
                );
              })}
              
              {/* Bypass Affiliates */}
              {attributeSelectors.qualityScoreCriteria.bypass && attributeSelectors.qualityScoreCriteria.bypass.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size={12}>
                    <Typography variant='h5'>Bypass Affiliates</Typography>
                  </Grid>
                </Grid>
              )}
              {attributeSelectors.qualityScoreCriteria.bypass && attributeSelectors.qualityScoreCriteria.bypass.map((bypass, index) => {
                const redisIndex = bypass.name?.replace('index_', '') || index.toString();
                const currentCap = currentCaps ? (currentCaps.bypass?.[redisIndex] || 0) : 0;
                
                return (
                  <Grid container spacing={2} sx={{ alignItems: 'start' }} key={index}>
                    <Grid size={2}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>{bypass.name || `Bypass ${index + 1}`}</FormLabel>
                    </Grid>
                    <Grid size={3}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Affiliates</FormLabel>
                      <MultiselectSummary value={bypass.bypass_affiliate || []} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Cap Amount</FormLabel>
                      <SummaryField value={bypass.cap || 'infinity'} />
                    </Grid>
                    <Grid size={1}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Current Cap</FormLabel>
                      <SummaryField value={currentCap} />
                    </Grid>
                    <Grid size={2}>
                      <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Failure Probability</FormLabel>
                      <SummaryField value={bypass.failureProbability ? `${(bypass.failureProbability * 100).toFixed(1)}%` : ''} />
                    </Grid>
                  </Grid>
                );
              })}
            </>
          )}
          {attributeSelectors.selectedQuestion && (
            <>
              <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                <Grid size={12}>
                  <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Criteria Question</FormLabel>
                  <SummaryField value={attributeSelectors.selectedQuestion} />
                </Grid>
              </Grid>
              {attributeSelectors.criteriaQuestionAnswers && (
                <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
                  <Grid size={12}>
                    <FormLabel component='p' sx={SUMMARY_LABEL_SX}>Criteria Question Answers</FormLabel>
                    <MultiselectSummary value={attributeSelectors.criteriaQuestionAnswers} />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default AttributeSummary;
